/**
 * bookingHelpers.js
 * Pure utility/helper functions for PassengerListPage.
 * No component state or API dependencies — safe to import anywhere.
 */
import moment from 'moment';

/**
 * Parses a passenger-count string like "A2C1I1" into a typed array.
 * Used by Akasa-Air and other vendors that encode pax counts this way.
 * @param {string} input - e.g. "A2C1I1"
 * @returns {Array<{type: string, count: number}>}
 */
export const parsePassengerString = (input) => {
    const typeMap = {
        A: 'ADT',
        C: 'CHD',
        I: 'INF'
    };

    const result = [];
    const regex = /([ACI])(\d*)/g;
    let match;

    while ((match = regex.exec(input)) !== null) {
        const letter = match[1];
        const countStr = match[2];
        const count = countStr ? parseInt(countStr) : 1;
        result.push({
            type: typeMap[letter],
            count: count
        });
    }

    return result;
};


// Jazeera infant age rules, checked against the flight dates (not today):
//   - Minimum: at least 14 days old on the outbound flight date.
//   - Maximum: under 2 years old on the inbound (return) flight date; one-way uses outbound.
// Returns an error message string when out of range, otherwise null.
/**
 * Validates AlJazeera infant date-of-birth against flight dates.
 * @param {moment.Moment} birthDate
 * @param {{ outbound: string, inbound?: string }} dates
 * @returns {string|null} Error message or null if valid
 */
export const getAljazeeraInfantDobError = (birthDate, dates) => {
    const outboundDate = moment(dates?.outbound);
    const inboundDate = dates?.inbound ? moment(dates.inbound) : outboundDate;
    // Minimum: must be born on or before (outbound - 14 days) to be at least 14 days old on outbound.
    const minBirthForOutbound = outboundDate.clone().subtract(14, 'days');
    if (birthDate.isAfter(minBirthForOutbound, 'day')) {
        return 'Infant must be at least 14 days old on the departure date.';
    }
    // Maximum: must be born after (inbound - 2 years) to be under 2 years old on the return date.
    const maxBirthForInbound = inboundDate.clone().subtract(2, 'years');
    if (!birthDate.isAfter(maxBirthForInbound, 'day')) {
        return 'Infant must be under 2 years old on the return date.';
    }
    return null;
};


/**
 * Formats a date value to YYYY-MM-DD string (Verteil API format).
 * Accepts Date objects, ISO strings, or "YYYY-MM-DD" strings (returned as-is).
 * @param {Date|string} dateValue
 * @returns {string}
 */
export function formatDateToDDMMYYYY(dateValue) {
    if (!dateValue) return '';
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
    }
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
}


/**
 * Calculates the price difference between original and new amounts.
 * Returns a negative value if originalAmount > differenceAmount (price went down),
 * positive if price went up.
 * @param {number} originalAmount
 * @param {number} differenceAmount
 * @returns {number}
 */
export const calculateDifference = (originalAmount, differenceAmount) => {
    const difference = originalAmount - differenceAmount;
    return difference < 0 ? Math.abs(difference) : -Math.abs(difference);
};


/**
 * Maps an AlJazeera / Navitaire passenger gender string to the
 * gender and title values expected by the Jazeera (J9) booking API.
 * Pure function — no component state dependency.
 *
 * @param {string} g - gender string from formik (e.g. 'Mr', 'Ms', 'Mrs', 'Master', 'Miss')
 * @returns {{ gender: string, title: string }}
 */
export const mapAljazeeraGenderTitle = (g) => {
    if (g === 'Mr') return { gender: 'Male', title: 'MR' };
    if (g === 'Ms') return { gender: 'Female', title: 'MS' };
    if (g === 'Mrs') return { gender: 'Female', title: 'MRS' };
    if (g === 'Master') return { gender: 'Male', title: 'MSTR' };
    if (g === 'Miss') return { gender: 'Female', title: 'MISS' };
    return { gender: g || '', title: '' };
};


/**
 * Returns 'Male' if the title implies male, otherwise 'Female'.
 * Covers: Mr, Master → Male; all other titles → Female.
 * @param {string} g - title/gender string from formik
 * @returns {'Male'|'Female'}
 */
export const getGenderFromTitle = (g) => {
    return (g === 'Mr' || g === 'Master') ? 'Male' : 'Female';
};


/**
 * Maps a formik gender/title string to the standard display title used by
 * most airline sources (proper case, 'Miss' as default fallback).
 * Mr→'Mr', Ms→'Ms', Mrs→'Mrs', Master→'Master', else→'Miss'
 * @param {string} g
 * @returns {string}
 */
export const getTitleFromGender = (g) => {
    if (g === 'Mr') return 'Mr';
    if (g === 'Ms') return 'Ms';
    if (g === 'Mrs') return 'Mrs';
    if (g === 'Master') return 'Master';
    return 'Miss';
};


/**
 * Same as getTitleFromGender but uses lowercase for 'ms' and 'master'.
 * Used by akasa-air (one-way / return) and travel-fusion sources.
 * Mr→'Mr', Ms→'ms', Mrs→'Mrs', Master→'master', else→'Miss'
 * @param {string} g
 * @returns {string}
 */
export const getTitleFromGenderLower = (g) => {
    if (g === 'Mr') return 'Mr';
    if (g === 'Ms') return 'ms';
    if (g === 'Mrs') return 'Mrs';
    if (g === 'Master') return 'master';
    return 'Miss';
};


/**
 * Like getTitleFromGender but falls back to 'Ms' (not 'Miss').
 * Used by certain sources where lowercase 'master' is needed.
 * Mr→'Mr', Ms→'Ms', Mrs→'Mrs', Master→'master', else→'Ms'
 * @param {string} g
 * @returns {string}
 */
export const getTitleFromGenderMsFallback = (g) => {
    if (g === 'Mr') return 'Mr';
    if (g === 'Ms') return 'Ms';
    if (g === 'Mrs') return 'Mrs';
    if (g === 'Master') return 'master';
    return 'Ms';
};


/**
 * Like getTitleFromGender but falls back to 'Ms' (not 'Miss'), with 'Master' capitalized.
 * Mr→'Mr', Ms→'Ms', Mrs→'Mrs', Master→'Master', else→'Ms'
 * @param {string} g
 * @returns {string}
 */
export const getTitleFromGenderMsFallbackUpper = (g) => {
    if (g === 'Mr') return 'Mr';
    if (g === 'Ms') return 'Ms';
    if (g === 'Mrs') return 'Mrs';
    if (g === 'Master') return 'Master';
    return 'Ms';
};


/**
 * Maps title to uppercase {gender, title} used by air-arabia / akasa booking API.
 * MASTER (not MSTR) is used here — differs from mapAljazeeraGenderTitle.
 * @param {string} g
 * @returns {{ gender: string, title: string }}
 */
export const getGenderTitleUppercase = (g) => {
    if (g === 'Mr') return { gender: 'Male', title: 'MR' };
    if (g === 'Ms') return { gender: 'Female', title: 'MS' };
    if (g === 'Mrs') return { gender: 'Female', title: 'MRS' };
    if (g === 'Master') return { gender: 'Male', title: 'MASTER' };
    if (g === 'Miss') return { gender: 'Female', title: 'MISS' };
    return { gender: g || '', title: '' };
};


/**
 * Verteil source: gender is stored as 'Male'/'Female' (not 'Mr'/'Ms').
 * Maps the stored gender value to a display title.
 * Male→'Mr', Female→'Mrs', Ms→'Ms', Master→'Master'
 * @param {string} g - stored gender ('Male', 'Female', 'Ms', 'Master')
 * @returns {string}
 */
export const getTitleFromStoredGender = (g) => {
    if (g === 'Male') return 'Mr';
    if (g === 'Ms') return 'Ms';
    if (g === 'Master') return 'Master';
    return 'Mrs';
};


/**
 * NDC/Amadeus title resolution.
 * For adults: Mr→'Mr', Ms→'Ms', Mrs→'Mrs', else→'Miss'
 * For non-adults (CHD/INF): Master→'Mstr', else→'Miss'
 * @param {string} g - gender string from formik
 * @param {boolean} isAdult - true for ADT, false for CHD/INF
 * @returns {string}
 */
export const getNdcTitle = (g, isAdult) => {
    if (!isAdult) return g === 'Master' ? 'Mstr' : 'Miss';
    if (g === 'Mr') return 'Mr';
    if (g === 'Ms') return 'Ms';
    if (g === 'Mrs') return 'Mrs';
    return 'Miss';
};


/**
 * NDC/Amadeus (Verteil variant): gender stored as 'Male'/'Female'.
 * For adults: Male→'Mr', else→'Mrs'
 * For non-adults: Male→'Mstr', else→'Miss'
 * @param {string} g - stored gender value ('Male', 'Female', etc.)
 * @param {boolean} isAdult
 * @returns {string}
 */
export const getNdcTitleFromStoredGender = (g, isAdult) => {
    if (!isAdult) return g === 'Male' ? 'Mstr' : 'Miss';
    return g === 'Male' ? 'Mr' : 'Mrs';
};


/**
 * Simple three-way title: Mr, Mrs, or Miss.
 * Used by ndc-portal source for display titles.
 * Mr→'Mr', Mrs→'Mrs', else→'Miss'
 * @param {string} g
 * @returns {string}
 */
export const getSimpleTitle = (g) => {
    if (g === 'Mr') return 'Mr';
    if (g === 'Mrs') return 'Mrs';
    return 'Miss';
};
